import { type DeliveryMethod } from '@alto/deliver_api/types/v1/delivery_methods/delivery_method';
import { Body, ExternalLink, XsSpacing } from '@alto/design-system';
import React, { useEffect } from 'react';
import { updateOrder } from '~shared/actions/cart';
import { ALTO_LOCATIONS_URL } from '~shared/constants';
import { useCartNextAvailableDate } from '~shared/features/cart/hooks/useCartNextAvailableDate';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { useCartDeliveryMethods } from '~shared/features/cart/queries/useCartDeliveryMethods';
import { ADDRESS_MESSAGES } from '~shared/features/checkout/constants';
import { getHasScheduleableDeliveryFeesInCart } from '~shared/features/checkout/selectors/getCart';
import { getOrderIndex, getOrdersByIndex } from '~shared/features/checkout/selectors/getOrdersByIndex';
import { type Order } from '~shared/features/checkout/types';
import { getAppliedDeliveryFee } from '~shared/features/shipment-fees/selectors/getAppliedDeliveryFee';
import { isFedEx, isUSPS } from '~shared/helpers/order';
import { useQueryShipmentFeesAvailable } from '~shared/queries';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useCheckoutAddressInfoKeys } from '../../../hooks/useCheckoutAddressInfoKeys';
import { feeTypeToFormValue, getPreSelectRadioValueForOrder } from '../../select-date-card/utils';
import { AddressInfoAlert } from './AddressInfoAlert';
import { HomeToSignConfirmation } from './HomeToSignConfirmation';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function AddressInfo() {
  const dispatch = useDispatchShared();
  const ordersByIndex = useSelectorShared(getOrdersByIndex);
  const orderIndex = useSelectorShared(getOrderIndex);
  const order: Order | undefined = ordersByIndex[orderIndex];
  const hasSchedulableDeliveryFeesInCart = useSelectorShared(getHasScheduleableDeliveryFeesInCart);
  const { dateToFeeHash } = useQueryShipmentFeesAvailable();
  const { earliestAvailableDate } = useCartNextAvailableDate();
  const { keys: addressInfoKeys, homeToSignConfirmationRequired } = useCheckoutAddressInfoKeys();
  const { deliveryMethods } = useCartDeliveryMethods();
  const { hasCompounds } = usePrescriptionsInCart();
  const deliveryMethod: DeliveryMethod | undefined = deliveryMethods[orderIndex];

  // hide the home to sign confirmation for compound meds
  const isMailedCompound = orderIndex === 0 && hasCompounds && deliveryMethod?.name === 'fedex_priority_overnight';

  // set the home to sign confirmation to unnecessary for shipment with compound med
  // home to sign confirmations are not passed onto compounding pharmacies
  useEffect(() => {
    if (isMailedCompound) {
      dispatch(updateOrder({ home_to_sign_confirmation: 'WAIVE' }));
    }
  }, [dispatch, isMailedCompound]);

  // determine if order date is not selected for mail orders
  const appliedDeliveryFee = useSelectorShared(getAppliedDeliveryFee);
  const preSelect = getPreSelectRadioValueForOrder({
    hasSchedulableDeliveryFeesInCart,
    orderDate: order?.date,
    dateToFeeHash,
    earliestAvailableDate,
    radioValue: appliedDeliveryFee ? feeTypeToFormValue[appliedDeliveryFee.fee_type] : '',
  });

  const isMailCarrierUnknown = preSelect === 'no-selection';

  if (addressInfoKeys.no_safe_place_specified) {
    // only applies to legacy addresses created before safe place was required
    return (
      <AddressInfoAlert type="error">
        <Body>{ADDRESS_MESSAGES.no_safe_place}</Body>
      </AddressInfoAlert>
    );
  }

  if (addressInfoKeys.out_of_zone) {
    return (
      <AddressInfoAlert type="error">
        <Body>
          This address is outside of our service area. Select an address within{' '}
          <ExternalLink href={ALTO_LOCATIONS_URL}>our service area</ExternalLink>.
        </Body>
      </AddressInfoAlert>
    );
  }

  if (deliveryMethod?.mode === 'MAIL') {
    const { message, title } = ADDRESS_MESSAGES.outside_courier_zone({
      isFedEx: isFedEx(deliveryMethod?.name),
      isUSPS: isUSPS(deliveryMethod?.name),
      isExpensive: !!addressInfoKeys.expensive,
      isMailCarrierUnknown,
      hasControls: !!addressInfoKeys.has_controls,
    });

    return (
      <>
        <AddressInfoAlert type={homeToSignConfirmationRequired ? 'warning' : 'neutral'}>
          <Body fontFamily="semibold">{title}</Body>
          <XsSpacing />
          <Body>{message}</Body>
        </AddressInfoAlert>
        {!addressInfoKeys.has_controls && !isMailedCompound && homeToSignConfirmationRequired ? (
          <HomeToSignConfirmation
            isSignatureRequired={homeToSignConfirmationRequired}
            orderIndex={orderIndex}
          />
        ) : null}
      </>
    );
  }

  if (addressInfoKeys.has_controls) {
    return (
      <AddressInfoAlert type="warning">
        <Body>{ADDRESS_MESSAGES.a_signature_required}</Body>
      </AddressInfoAlert>
    );
  }

  if (addressInfoKeys.expensive) {
    return (
      <>
        <AddressInfoAlert type="warning">
          <Body>This order contains an expensive medication.</Body>
        </AddressInfoAlert>
        {!isMailedCompound ? (
          <HomeToSignConfirmation
            isSignatureRequired={homeToSignConfirmationRequired}
            orderIndex={orderIndex}
          />
        ) : null}
      </>
    );
  }

  if (addressInfoKeys.no_safe_place) {
    return (
      <>
        <AddressInfoAlert type="warning">
          <Body>{ADDRESS_MESSAGES.no_safe_place}</Body>
        </AddressInfoAlert>
        {!isMailedCompound ? (
          <HomeToSignConfirmation
            isSignatureRequired={homeToSignConfirmationRequired}
            orderIndex={orderIndex}
          />
        ) : null}
      </>
    );
  }

  return (
    <AddressInfoAlert>
      <Body>{ADDRESS_MESSAGES.no_need_to_be_home}</Body>
    </AddressInfoAlert>
  );
}
